import React, { useEffect, useState } from 'react';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import { Wallet, Clock, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'In attesa', color: 'bg-gray-100 text-gray-700' },
  accrued: { label: 'Maturata', color: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Pagata', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annullata', color: 'bg-red-50 text-red-700' },
};

const AgentCommissions = () => {
  const { agentToken } = useAgentAuth();
  const [commissions, setCommissions] = useState([]);
  const [totals, setTotals] = useState({ pending: 0, accrued: 0, paid: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      const r = await fetch(`${API}/api/agent/commissions?${params}`, {
        headers: { Authorization: `Bearer ${agentToken}` },
      });
      const j = await r.json();
      setCommissions(j.data || []);
      setTotals(j.totals || {});
    } finally { setLoading(false); }
  };

  useEffect(() => { if (agentToken) load(); /* eslint-disable-next-line */ }, [agentToken, filter]);

  const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  return (
    <AgentLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Le mie provvigioni</h1>
        <p className="text-sm text-gray-600 mt-1">Storico completo delle tue commissioni.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-2xl border-l-4 border-gray-300">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase text-gray-500 font-semibold">In attesa</p>
              <p className="mt-2 text-2xl font-bold text-gray-900 tabular-nums">{eur(totals.pending)}</p>
            </div>
            <Clock className="w-5 h-5 text-gray-400" />
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border-l-4 border-amber-500">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase text-gray-500 font-semibold">Maturate</p>
              <p className="mt-2 text-2xl font-bold text-amber-700 tabular-nums">{eur(totals.accrued)}</p>
            </div>
            <Wallet className="w-5 h-5 text-amber-500" />
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border-l-4 border-green-500">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase text-gray-500 font-semibold">Pagate</p>
              <p className="mt-2 text-2xl font-bold text-green-700 tabular-nums">{eur(totals.paid)}</p>
            </div>
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {['', 'pending', 'accrued', 'paid', 'cancelled'].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
              filter === s ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-purple-300'
            }`}>
            {s === '' ? 'Tutte' : STATUS_LABEL[s]?.label || s}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Caricamento…</div>
        ) : commissions.length === 0 ? (
          <div className="py-16 text-center text-gray-500">Nessuna provvigione con questo filtro.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-purple-50/50 text-purple-700 uppercase text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Ordine</th>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-right px-4 py-3">Totale ordine</th>
                <th className="text-right px-4 py-3">%</th>
                <th className="text-right px-4 py-3">Commissione</th>
                <th className="text-center px-4 py-3">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {commissions.map((c) => {
                const s = STATUS_LABEL[c.status] || { label: c.status, color: 'bg-gray-100' };
                return (
                  <tr key={c.id} className="hover:bg-purple-50/40" data-testid={`commission-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-purple-700">{c.orderNumber}</td>
                    <td className="px-4 py-3 text-gray-800">{c.customerName}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{eur(c.orderTotal)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{c.rate}%</td>
                    <td className="px-4 py-3 text-right font-semibold text-purple-700 tabular-nums">{eur(c.amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`}>
                        {s.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentCommissions;
